import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "modules/authentication/hooks/use-auth";
import {
  AcquirePlusDialog,
  type AcquirePlusDialogProps,
} from "modules/subscription/components/AcquirePlusDialog";

/**
 * Props for {@link PlanRequiredErrorView}.
 */
export interface PlanRequiredErrorViewProps {
  /** Cut 3: which quota was exceeded (from the backend 403 body). */
  reason?: AcquirePlusDialogProps["reason"];
  /** Cut 3: usage metadata for quota-specific copy. */
  quota?: AcquirePlusDialogProps["quota"];
}

/**
 * Renders the 403 `plan_required` UX surface for the checkout pages.
 *
 * - Guest (`isLogged === false`): calls `requireLoginAction()` and renders nothing.
 *   The global `LoginRegisterDialog` (mounted in `AuthProvider`) opens.
 * - Authenticated non-plus: mounts `<AcquirePlusDialog open />`.
 *   Plus users cannot reach this branch (the backend bypasses them, see
 *   `cart-checkout` FR-12).
 *
 * When the dialog requests close (X button, "Continuar sin Carriapp Plus",
 * or post-purchase confirmation) the view navigates to `/store` so the
 * user lands on the catalog instead of being stranded on the checkout page.
 */
export const PlanRequiredErrorView: React.FC<PlanRequiredErrorViewProps> = ({
    reason,
    quota,
}) => {
    const { isLogged, requireLoginAction } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (!isLogged) {
            requireLoginAction();
        }
    }, [isLogged, requireLoginAction]);

    if (!isLogged) {
        return null;
    }

    return (
        <AcquirePlusDialog
            open
            onOpenChange={(nextOpen) => {
                if (!nextOpen) {
                    navigate("/store");
                }
            }}
            reason={reason}
            quota={quota}
        />
    );
};
